import React from 'react'
import { Grid, Theme, Typography } from '@material-ui/core'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import useFormikHelper from '../useFormikHelper'
import { ModificationValues } from '../enum'
import {
  biTubeSequencePreviewName,
  useProductFromQueryParam,
  SequenceType,
} from '../ProductsConfiguration'
import { BiTubeProduct } from '../../BiTube/enum'
import { formatOverhangSequenceRedesign } from '../../../utils/formatOverhangSequence'
import MiRNASequencePreview from './MirnaSequencePreview'
const useStyle = makeStyles((theme: Theme) => ({
  textStyle: {
    fontSize: theme.typography.pxToRem(16),
  },
  sequenceLabel: {
    fontWeight: 'bold',
    marginRight: theme.spacing(2),
  },
  sequence: {
    wordBreak: 'break-word',
    display: 'flex',
  },
}))

const BiTubeSequencePreview = () => {
  const classes = useStyle()
  const formik = useFormikHelper()
  const {
    fiveMod,
    sequence,
    threeMod,
    antisenceThreeMod,
    antisenceFiveMod,
    antisenceSequence,
    customerOverhang,
    customerAntisenseOverHang,
  } = formik.selectedOligoValues

  const product = useProductFromQueryParam()
  const isMirna = product === BiTubeProduct.mirna
  const previewText = (
    <FormattedMessage
      id="EMPTY_SEQUENCE_PREVIEW_TEXT"
      defaultMessage="Start typing sequence above to see the preview."
    />
  )

  const renderSequence = ({
    sequenceData,
    error,
    fiveModValue,
    threeModValue,
    customerOverhangValue,
  }: {
    sequenceData?: string
    error?: string
    fiveModValue?: ModificationValues
    threeModValue?: ModificationValues
    customerOverhangValue?: ModificationValues
  }) => {
    if (error) return <>{'--'}</>
    if (!sequenceData) return <>{previewText}</>

    const appendOverhang =
      customerOverhangValue && customerOverhangValue !== ModificationValues.None
        ? formatOverhangSequenceRedesign(customerOverhangValue)
        : undefined

    return (
      <div style={{ display: 'flex' }}>
        {fiveModValue !== ModificationValues.None && fiveModValue}
        {sequenceData}
        {appendOverhang}
        {threeModValue !== ModificationValues.None && threeModValue}
      </div>
    )
  }

  const [primarySequence, secondarySequence] =
    product === BiTubeProduct.mirna
      ? [antisenceSequence, sequence]
      : [sequence, antisenceSequence]

  const [primaryError, secondaryError] =
    product === BiTubeProduct.mirna
      ? [formik.errors?.antisenceSequence, formik.errors?.sequence]
      : [formik.errors?.sequence, formik.errors?.antisenceSequence]

  const [primaryFiveMod, secondaryFiveMod] =
    product === BiTubeProduct.mirna
      ? [antisenceFiveMod, fiveMod]
      : [fiveMod, antisenceFiveMod]

  const [primaryThreeMod, secondaryThreeMod] =
    product === BiTubeProduct.mirna
      ? [antisenceThreeMod, threeMod]
      : [threeMod, antisenceThreeMod]

  const [primaryOverhang, secondaryOverhang] =
    product === BiTubeProduct.mirna
      ? [customerAntisenseOverHang, customerOverhang]
      : [customerOverhang, customerAntisenseOverHang]
  if (isMirna) return <MiRNASequencePreview />
  return (
    <Grid container className={classes.textStyle}>
      <Grid item xs={12} className={classes.sequence}>
        <Grid container spacing={0}>
          <Grid item xs={2}>
            <Typography className={classes.sequenceLabel}>
              {biTubeSequencePreviewName(product, SequenceType.Primary)}:
            </Typography>
          </Grid>
          <Grid item xs={10}>
            {renderSequence({
              sequenceData: primarySequence,
              error: primaryError,
              fiveModValue: primaryFiveMod as ModificationValues | undefined,
              threeModValue: primaryThreeMod as ModificationValues | undefined,
              customerOverhangValue: primaryOverhang as
                | ModificationValues
                | undefined,
            })}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} className={classes.sequence}>
        <Grid container spacing={0}>
          <Grid item xs={2}>
            <Typography className={classes.sequenceLabel}>
              {biTubeSequencePreviewName(product, SequenceType.Secondary)}:
            </Typography>
          </Grid>
          <Grid item xs={10}>
            {renderSequence({
              sequenceData: secondarySequence,
              error: secondaryError,
              fiveModValue: secondaryFiveMod as ModificationValues | undefined,
              threeModValue: secondaryThreeMod as
                | ModificationValues
                | undefined,
              customerOverhangValue: secondaryOverhang as
                | ModificationValues
                | undefined,
            })}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default BiTubeSequencePreview
